import PusherJs from "pusher-js";
import type { Channel } from "pusher-js";
import { runtimeConfig } from "./runtime-config";

let pusherInstance: PusherJs | null = null;

function getPusherInstance(): PusherJs | null {
  if (pusherInstance) return pusherInstance;
  const key = runtimeConfig.pusherKey;
  const cluster = runtimeConfig.pusherCluster;
  if (!key || !cluster) return null;
  pusherInstance = new PusherJs(key, {
    cluster,
    authEndpoint: "/api/pusher/auth",
    auth: { headers: {} },
  });
  return pusherInstance;
}

const subscribedChannels = new Map<string, Channel>();
const channelRefCounts = new Map<string, number>();

function getChannel(channelName: string): Channel | null {
  const pusher = getPusherInstance();
  if (!pusher) return null;
  const existing = subscribedChannels.get(channelName);
  if (existing) {
    channelRefCounts.set(channelName, (channelRefCounts.get(channelName) ?? 0) + 1);
    return existing;
  }
  const channel = pusher.subscribe(channelName);
  subscribedChannels.set(channelName, channel);
  channelRefCounts.set(channelName, 1);
  return channel;
}

function releaseChannel(channelName: string): void {
  const pusher = getPusherInstance();
  if (!pusher) return;
  const count = channelRefCounts.get(channelName) ?? 0;
  if (count <= 1) {
    pusher.unsubscribe(channelName);
    subscribedChannels.delete(channelName);
    channelRefCounts.delete(channelName);
  } else {
    channelRefCounts.set(channelName, count - 1);
  }
}

export function getTenantChannel(tenantId: string): Channel | null {
  return getChannel(`private-tenant-${tenantId}-operations`);
}

export function releaseTenantChannel(tenantId: string): void {
  releaseChannel(`private-tenant-${tenantId}-operations`);
}

export function getConversationsChannel(tenantId: string): Channel | null {
  return getChannel(`private-tenant-${tenantId}-conversations`);
}

export function releaseConversationsChannel(tenantId: string): void {
  releaseChannel(`private-tenant-${tenantId}-conversations`);
}

export function getSmsCampaignChannel(tenantId: string, campaignId: string): Channel | null {
  return getChannel(`private-tenant-${tenantId}-sms-${campaignId}`);
}

export function releaseSmsCampaignChannel(tenantId: string, campaignId: string): void {
  releaseChannel(`private-tenant-${tenantId}-sms-${campaignId}`);
}

export function subscribeToReconnect(handler: () => void): () => void {
  const pusher = getPusherInstance();
  if (!pusher) return () => {};
  const stateHandler = ({ previous, current }: { previous: string; current: string }) => {
    if (current === "connected" && previous !== "connected") {
      handler();
    }
  };
  pusher.connection.bind("state_change", stateHandler);
  return () => pusher.connection.unbind("state_change", stateHandler);
}

export function getTeamChatPusherChannel(channelId: string): Channel | null {
  return getChannel(`private-team-chat-${channelId}`);
}

export function releaseTeamChatPusherChannel(channelId: string): void {
  releaseChannel(`private-team-chat-${channelId}`);
}

export function getSupportInboxChannel(): Channel | null {
  return getChannel("private-support-inbox");
}

export function releaseSupportInboxChannel(): void {
  releaseChannel("private-support-inbox");
}

export const getSupportInboxPusherChannel = getSupportInboxChannel;
export const releaseSupportInboxPusherChannel = releaseSupportInboxChannel;

export function getSupportSessionChannel(sessionId: string): Channel | null {
  return getChannel(`private-support-session-${sessionId}`);
}

export function releaseSupportSessionChannel(sessionId: string): void {
  releaseChannel(`private-support-session-${sessionId}`);
}

export function getLivechatSessionChannel(sessionId: string): Channel | null {
  return getChannel(`private-livechat-session-${sessionId}`);
}

export function releaseLivechatSessionChannel(sessionId: string): void {
  releaseChannel(`private-livechat-session-${sessionId}`);
}
